import { createApiClient } from '../oidc-api';
import { 
  ApplicationCountByStatus, 
  ApplicationCount, 
  ChatHistoryCountByStatus, 
  ChatHistoryCount, 
  UserCountByStatus, 
  UserCount,
  UserCreationStatsDto,
  ChatHistoryByVendorStatsDto,
  ChatHistoryByApplicationStatsDto,
  ChatHistoryCreationStatsDto
} from './types';

// Helper function to format date to yyyy-MM-dd HH:mm:ss
const formatDateForAPI = (dateString: string): string => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// Note: The following endpoints are not in swagger.yaml and might not exist
// Keeping them for backward compatibility but they should be verified

// Applications Analytics (TO BE VERIFIED)
export const getApplicationsCountByStatus = async (user: any): Promise<ApplicationCountByStatus[]> => {
  const api = createApiClient(user);
  try {
    const response = await api.get('/v1/analytics/applications/count-by-status');
    return response.data;
  } catch (error) {
    console.warn('Applications count by status endpoint might not exist:', error);
    return [];
  }
};

export const getApplicationsCount = async (user: any): Promise<ApplicationCount> => {
  const api = createApiClient(user);
  try {
    const response = await api.get('/v1/analytics/applications/count');
    return response.data;
  } catch (error) {
    console.warn('Applications count endpoint might not exist:', error);
    return { total: 0 };
  }
};

// Chat History Analytics (TO BE VERIFIED)
export const getChatHistoryCountByStatus = async (user: any): Promise<ChatHistoryCountByStatus[]> => {
  const api = createApiClient(user);
  try {
    const response = await api.get('/v1/analytics/chat-history/count-by-status');
    return response.data;
  } catch (error) {
    console.warn('Chat history count by status endpoint might not exist:', error);
    return [];
  }
};

export const getChatHistoryCount = async (user: any): Promise<ChatHistoryCount> => {
  const api = createApiClient(user);
  try {
    const response = await api.get('/v1/analytics/chat-history/count');
    return response.data;
  } catch (error) {
    console.warn('Chat history count endpoint might not exist:', error);
    return { total: 0 };
  }
};

// Users Analytics (TO BE VERIFIED)
export const getUsersCountByStatus = async (user: any): Promise<UserCountByStatus[]> => {
  const api = createApiClient(user);
  try {
    const response = await api.get('/v1/analytics/users/count-by-status');
    return response.data;
  } catch (error) {
    console.warn('Users count by status endpoint might not exist:', error);
    return [];
  }
};

export const getUsersCount = async (user: any): Promise<UserCount> => {
  const api = createApiClient(user);
  try {
    const response = await api.get('/v1/analytics/users/count');
    return response.data;
  } catch (error) {
    console.warn('Users count endpoint might not exist:', error);
    return { total: 0 };
  }
};

// Analytics Endpoints from swagger.yaml

// Get user count by date - /v1/analytics/user/count_by_date
export const getUserCountByDate = async (
  user: any, 
  startDate?: string, 
  endDate?: string
): Promise<UserCreationStatsDto[]> => {
  const api = createApiClient(user);
  const params = new URLSearchParams();
  
  // Pass dates in yyyy-MM-dd HH:mm:ss format
  if (startDate) {
    params.append('start_date', formatDateForAPI(startDate));
  }
  if (endDate) {
    params.append('end_date', formatDateForAPI(endDate));
  }
  
  const queryString = params.toString() ? `?${params.toString()}` : '';
  console.log('getUserCountByDate URL:', `/v1/analytics/user/count_by_date${queryString}`);
  const response = await api.get(`/v1/analytics/user/count_by_date${queryString}`);
  return response; // The response is already the array
};

// Get chat history count by vendor - /v1/analytics/chat_history/count_by_vendor
export const getChatHistoryCountByVendor = async (user: any): Promise<ChatHistoryByVendorStatsDto[]> => {
  const api = createApiClient(user);
  const response = await api.get('/v1/analytics/chat_history/count_by_vendor');
  return response; // The response is already the array
};

// Get chat history count by date and application - /v1/analytics/chat_history/count_by_date_and_application
export const getChatHistoryCountByDateAndApplication = async (
  user: any,
  startDate?: string,
  endDate?: string
): Promise<ChatHistoryByApplicationStatsDto[]> => {
  const api = createApiClient(user);
  const params = new URLSearchParams();
  
  // Pass dates in yyyy-MM-dd HH:mm:ss format
  if (startDate) {
    params.append('start_date', formatDateForAPI(startDate));
  }
  if (endDate) {
    params.append('end_date', formatDateForAPI(endDate));
  }
  
  const queryString = params.toString() ? `?${params.toString()}` : '';
  console.log('getChatHistoryCountByDateAndApplication URL:', `/v1/analytics/chat_history/count_by_date_and_application${queryString}`);
  const response = await api.get(`/v1/analytics/chat_history/count_by_date_and_application${queryString}`);
  return response; // The response is already the array
};

// Get chat history count by date - /v1/analytics/chat_history/count_by_date
export const getChatHistoryCountByDate = async (
  user: any,
  startDate?: string,
  endDate?: string
): Promise<ChatHistoryCreationStatsDto[]> => {
  const api = createApiClient(user);
  const params = new URLSearchParams();
  
  // Pass dates in yyyy-MM-dd HH:mm:ss format
  if (startDate) {
    params.append('start_date', formatDateForAPI(startDate));
  }
  if (endDate) {
    params.append('end_date', formatDateForAPI(endDate));
  }
  
  const queryString = params.toString() ? `?${params.toString()}` : '';
  console.log('getChatHistoryCountByDate URL:', `/v1/analytics/chat_history/count_by_date${queryString}`);
  const response = await api.get(`/v1/analytics/chat_history/count_by_date${queryString}`);
  return response; // The response is already the array
};